/**
 * Body chart for the HUMINT body map (PRD-0032 UC-7), generated from the canvas kit's
 * `bodymap.json`. Keep the path data exact: pins, hit targets and the preview all share it.
 *
 * - Laterality is always the SUBJECT'S. Front view: the subject's left is drawn on the
 *   viewer's right (x > 120). Back view: the subject's left is on the viewer's left.
 * - Front and back share one silhouette, so a view toggle can cross-fade without a jump.
 * - `centroid` is the pole of inaccessibility (a safe pin spot), not the area centroid.
 * - `inscribedD` is the diameter of the largest circle inside the region (viewBox px). The
 *   hands and feet are too small for a 48 px target at phone width: BodyMap zooms onto them
 *   first (body-map-model `ZOOM_AREAS`).
 * - `ncicHint` is an UNOFFICIAL location hint modelled on NCIC / ANSI-NIST Type-10 SMT
 *   location tokens. Verify against the NCIC code manual before storing or exporting.
 *
 * Labels are NOT stored here: use t(bodyRegionLabelKey(id)) from ./region-labels, which does
 * not pull this path table into the bundle.
 */
import type { BodyRegionGroup, BodyRegionId, BodyView, Laterality } from '../types'

export interface BodyRegion {
  id: BodyRegionId
  view: BodyView
  laterality: Laterality
  group: BodyRegionGroup
  /** One closed cubic-Bezier path in the BODY_VIEWBOX coordinate space. */
  d: string
  centroid: { x: number; y: number }
  ncicHint: string
  inscribedD: number
}

export const BODY_VIEWBOX = { width: 240, height: 560, value: '0 0 240 560' } as const

/** Whole-figure silhouette (soft backdrop behind the regions); front and back share it. */
export const BODY_OUTLINE =
  'M119.9 18c-1.4 0-3 .4-4.4 .6c-7.1 .9-12.4 2.8-16.6 8.9c-1.2 1.7-1.8 3.9-2.3 6c-2.8 10.8-.8 26 3.2 36.3c.8 2 1.6 4.1 2.5 6c.9 1.8 2.9 3.7 3.1 5.7c.3 2.7-.1 5.7-.4 8.4c-.2 1.5-.1 3.6-.7 5c-2.6 5.4-9.2 7-14.5 8.6c-4.6 1.4-9.4 2.5-13.9 4.2c-2.6 1-5.2 2.5-7.8 3.3c-1.4 .5-3.1 .3-4.5 .4c-5.2 .6-9.5 4.7-11.7 9.2c-.6 1.4-.9 2.9-1.3 4.4c-4 14.2-3.8 29.3-6.1 44c-.8 5.6-2 11.1-2.9 16.7c-1.2 8.4-1.8 16.9-3.4 25.3c-1.4 8-3.7 15.9-5.1 23.9c-1.8 10-2.5 20.2-3.4 30.3c-.5 5.2-1.8 23.5-3.4 27.3c-.4 .9-1.2 1.6-1.6 2.5c-.5 1-.6 2.3-1.1 3.3c-.5 1-1.5 1.8-2 2.8c-2.2 4.2-3.9 8.5-5.9 12.8c-1.2 2.6-5.6 11-5.8 13.2c-.1 1 .4 2 1.1 2.7c.6 .6 1.4 .8 2.3 .8c3.2 .2 4.6-5.9 6.1-5c.3 .2-.6 4.6-.8 5.3c-.5 2.5-3.3 15.1-2.8 16.7c.2 .8 .8 1.5 1.5 1.8c4 1.8 4.5-5.8 5.1-8c.1-.6 .4-3.2 1.1-3c.7 .1 .1 3.2 .1 3.7c-.2 2.2-1.2 7.6-1 9.5c.2 1 .9 1.9 1.8 2.2c5.8 2 3.6-12.4 5.4-13.2c1-.4 .6 2.6 .6 3c-.1 2.5-1.3 8.4 2.5 8.6c1 0 2-.7 2.4-1.5c1-2 .7-7.9 .8-10.4c.1-.7 0-4.2 .8-4.1c.1 0 .3 .1 .4 .2c1.1 1.9-1.3 9.2 2.8 9.6c.6 .1 1.4-.2 1.8-.6c1.1-1.2 .6-3.9 .5-5.4c0-3.4-.6-12.1-.1-15c.1-1.1 .6-2.2 .7-3.4c.5-7.6 .3-15.3 .2-23c0-1.4-.7-3.2-.6-4.4c.1-3.5 2.1-9 3.1-12.6c2.2-7.7 4.3-15.4 6.4-23.1c2.5-9 4.7-18.1 6.5-27.2c1.1-5.2 1.8-10.6 3-15.7c2.5-10.2 5.7-20.3 7.5-30.6c1-5.8 2.1-11.5 3.1-17.2c.2-1.5 .4-5.7 2.8-5c2 .6 1.5 3.8 1.5 5.4c.2 5.8 .4 11.6 .8 17.5c.8 9.2 2.4 20.3 1.7 29.4c-1 12.6-5.4 24.3-8.6 36.4c-.8 3.1-1.5 6.2-2.2 9.3c-3.4 14.4-1.5 30.2 .1 44.7c1.7 14.1 3.5 28.1 5.7 42.1c1.7 10.7 4.1 21.3 5.5 32c1.2 8.7 2.2 17.7 1.6 26.5c-.5 7.6-1.9 15.2-2 22.9c-.1 8.7 1.2 17.6 2.2 26.3c1.2 9 2.3 17.9 3.4 26.8c1.1 8.5 2.1 17.3 2.1 25.9c0 2.4 0 5.1-.4 7.5c-.3 2-1.1 3.9-1.4 5.9c-.2 1.8 .4 3.6 .3 5.4c-.1 1-.7 2-1 2.9c-1.6 4.3-5.5 11.9-2.1 16.1c2.6 3.1 23.1 3.8 24.5-3c.7-3.4 .1-7.6-.7-10.9c-.5-1.8-1.1-3.5-1.3-5.4c-.5-4 1.1-7.9 1-11.9c-.1-1.9-.6-4-.9-5.9c-1-6.7-2.4-13.5-2.3-20.4c.4-17.5 4-34.7 5.6-52.1c.3-4.1 .6-8.4 .5-12.5c-.2-4.7-.6-9.3-.4-14c.2-5.6 1.4-11.2 1.8-16.9c.4-6.5 .4-13 .9-19.5c.3-5.1 .9-10.3 1.3-15.4c1.3-14.4 2.6-28.9 4.4-43.3c.3-2.3 .9-10.2 1.8-11.8c.3-.6 1-1 1.7-1c.6 .1 1.2 .5 1.5 1.1c.9 1.8 1.6 9.9 1.9 12.4c1.7 14.7 3.2 29.4 4.4 44.2c.5 5.2 1 10.3 1.3 15.5c.4 5.9 .4 12 .8 17.9c.4 5.7 1.6 11.3 1.8 16.9c.2 4.7-.3 9.4-.4 14c-.1 4 .2 8.1 .5 12c1.1 17.8 6.1 35.3 5.6 53.2c-.2 6.5-1.3 12.9-2.2 19.3c-.5 2.8-1.1 5.6-.9 8.4c.3 3.2 1.2 6.2 1 9.5c-.3 4.3-4.1 14.1-1.6 18.1c2.5 3.9 12 4 16.1 3.7c4.1-.4 8.5-.5 9.2-5.5c.7-5.3-3.4-10.6-4.3-15.3c-.4-1.7 .3-3.7 .2-5.5c-.1-2.5-1.3-4.9-1.5-7.3c-.7-7 0-14 .7-21c.9-10.3 2.3-20.5 3.5-30.8c1.4-10.9 3.4-22.8 3.2-33.8c-.2-8-1.5-15.9-2-23.9c-.5-7 .6-18.3 1.6-25.4c1.4-10.7 3.8-21.3 5.5-32c2.1-13.5 3.9-27.1 5.5-40.7c1.4-11.3 3-22.9 2-34.3c-1.5-17.5-8.8-33.5-11.7-50.6c-1.9-11.6-.2-23.3 .8-34.9c.4-5.5 .6-11 .8-16.5c.1-1.5-.3-5.7 .5-6.9c.3-.4 .8-.7 1.2-.8c2.5-.4 2.5 4 2.8 5.6c1.4 7.5 2.4 15.2 4.2 22.6c1 4.2 1.9 8.5 3 12.7c1 4 2.4 7.9 3.3 12c1.2 5.2 1.9 10.5 3 15.7c1.3 6.2 2.8 12.4 4.3 18.5c2.5 10 5.3 20 8.1 29.9c.8 2.9 3.7 12.1 3.7 14.6c-.1 1.1-.6 2.2-.7 3.4c-.3 5.1-.5 21 .5 25.4c1.1 4.8 .3 11.5 .2 16.5c-.1 1.7-.8 5 1.1 6.1c4.7 2.6 3.6-7 4.1-9c0-.3 .1-.7 .5-.6c.7 .1 .6 3.1 .6 3.7c.2 2.4 0 9.2 .9 10.9c.3 .5 1 1.1 1.6 1.2c6 1.7 2.1-10.8 3.6-11.3c.8-.4 1 2.9 1.1 3.4c.2 1.8 .5 7 1.3 8.4c.5 .9 1.4 1.5 2.4 1.4c.9 0 2-.6 2.4-1.5c.7-1.6-.4-7.9-.6-9.9c-.1-.6-.8-4.2 .1-4.2c1.6 .2 1 13.9 6.2 11c.7-.4 1.3-1 1.5-1.8c.4-1.6-2.4-13.8-2.9-16.3c-.1-.8-1.3-5.5-.6-5.7c1.6-.3 2.6 5.3 6.1 5.1c.8 0 1.6-.3 2.2-.9c.7-.8 1.1-1.7 1-2.7c-.4-2.6-4.8-10.9-6.1-13.7c-1.9-4.1-3.5-8.4-5.6-12.3c-.5-.9-1.2-1.6-1.8-2.4c-.7-1.1-.8-2.6-1.4-3.7c-.4-.9-1.2-1.6-1.5-2.6c-1.8-5-2.9-21.6-3.4-27.7c-1.1-11.3-2.1-22.6-4.1-33.8c-1.3-7.2-3.5-14.2-4.7-21.5c-1.3-7.8-1.9-15.8-3.2-23.8c-.8-5.5-2.1-11.1-2.9-16.7c-1.8-13-2.4-38.2-7.8-49.2c-2-4.1-6.4-7.2-10.9-8.1c-1.4-.3-3-.1-4.5-.4c-2.8-.6-5.5-2.5-8.2-3.5c-4.6-1.6-9.3-2.7-13.9-4.1c-4.3-1.2-12.7-3.7-14.3-8.3c-.6-1.6-1.6-12.3-1.2-13.9c.3-1.6 2-3.3 2.7-4.8c1.2-2.3 2.4-4.8 3.1-7.3c5-15.9 10.4-44.6-11.6-50c-2.3-.5-6.5-1.2-8.9-1.2z'

export const BODY_REGIONS: Record<BodyView, readonly BodyRegion[]> = {
  front: [
    {
      id: 'head_face',
      view: 'front',
      laterality: 'center',
      group: 'head_neck',
      d: 'M120.1 19.4c-1.4-.1-6.9 .7-8.4 1c-2.7 .5-5.1 1.6-7.3 3.2c-2 1.5-3.8 3.3-5 5.5c-1.1 2.2-1.7 4.7-2.2 7.1c-1.4 8-.7 16 1.2 23.8c.6 2.7 1.3 5.5 2.2 8.2c1.9 5.8 5.8 14.1 11.3 17.1c1.5 .8 3.2 1.1 4.8 1.4c1.1 .2 2.4 .6 3.5 .5c2.2-.1 6.3-1 8.2-2c6.3-3.8 10.3-13.9 12.1-20.7c2.4-9.1 4-18.3 2.4-27.6c-1.3-7.3-4.2-12.9-11.5-15.7c-2-.7-9.1-1.8-11.3-1.8z',
      centroid: { x: 119.9, y: 44.7 },
      ncicHint: 'FACE',
      inscribedD: 46.8,
    },
    {
      id: 'neck_front',
      view: 'front',
      laterality: 'center',
      group: 'head_neck',
      d: 'M109 87c-3.6-.1-2.8 5.5-3.3 7.9c-.2 1-.8 1.8-.5 2.9c.9 2.6 8.2 10.2 10.6 11.9c1.2 .9 2.9 2.1 4.5 1.9c4-.4 12.4-9.8 14.3-13.4c.7-1.4-.3-2.9-.4-4.3c-.2-1.6-.1-4.7-1-5.9c-.5-.7-1.3-1-2.2-1c-1.1 0-2.1 .9-3.2 1.3c-1.2 .4-6.7 1.6-7.8 1.6c-1.1 0-6.2-1.1-7.4-1.5c-1.2-.4-2.4-1.4-3.6-1.4z',
      centroid: { x: 119.9, y: 99.9 },
      ncicHint: 'NECK',
      inscribedD: 20.0,
    },
    {
      id: 'shoulder_right',
      view: 'front',
      laterality: 'right',
      group: 'arm',
      d: 'M102.4 100.2c-1.3 0-3.3 1.4-4.6 2c-3.5 1.4-7.2 2.5-10.9 3.5c-4.1 1.2-8.3 2.3-12.3 3.9c-2.1 .8-4.4 2.2-6.4 2.8c-1 .3-2 .1-3 .1c-3.8 .3-7.5 2.2-9.9 5.2c-4.3 5-5.9 18.2-6.6 24.8c-.6 5.3-1.1 10.6-1.6 15.9c-.2 1.7-1.1 4.3 .5 5.7c1.4 1.2 4.1 .5 5.8 .2c4.6-.6 9.1-1.7 13.5-3.3c1.4-.5 4.1-1.1 4.9-2.4c.2-.4 .4-.9 .4-1.4c0-1.6-1.2-7.2-1.5-9.3c-.9-7.3-2.6-20.8 2.5-26.8c2-2.3 5.6-3.2 8.5-3.8c6.6-1.6 13.4-2.2 20.2-3.3c1.8-.2 6.9-.7 8.2-1.7c.9-.8 1.3-2.1 .7-3.2c-.5-1.1-2.4-2.7-3.2-3.7c-1.2-1.4-2.2-3.1-3.4-4.4c-.5-.4-1.1-.7-1.8-.8z',
      centroid: { x: 59.5, y: 150.9 },
      ncicHint: 'R SHLD',
      inscribedD: 23.2,
    },
    {
      id: 'shoulder_left',
      view: 'front',
      laterality: 'left',
      group: 'arm',
      d: 'M137.5 100.2c-.5 .1-1 .3-1.4 .6c-1 .7-3.3 4.2-4.4 5.4c-.8 .9-2.1 1.9-2.6 3c-.5 1.3 .1 2.8 1.3 3.4c1.6 .7 6.4 1.2 8.4 1.5c5.9 1 11.8 1.6 17.7 2.8c3.4 .6 8.1 1.6 10.4 4.3c5 6.1 3.3 19.5 2.4 26.8c-.3 2-1.6 7.9-1.5 9.4c0 .5 .2 .9 .5 1.3c.9 1.2 3.5 1.9 4.9 2.4c4.4 1.5 8.9 2.7 13.6 3.3c1.7 .2 4.4 .9 5.7-.4c1.5-1.4 .6-4 .4-5.8c-.5-5.1-.9-10.2-1.5-15.3c-.8-6.8-2.4-20.2-6.7-25.3c-2.4-2.8-5.8-4.7-9.5-5c-1.1-.2-2.4 .1-3.5-.2c-2.2-.8-4.2-2.1-6.4-2.9c-3.8-1.5-7.9-2.6-11.9-3.7c-3.7-1-7.3-2.1-10.9-3.5c-1.3-.5-3.8-2.2-5-2.1z',
      centroid: { x: 180.6, y: 150.9 },
      ncicHint: 'L SHLD',
      inscribedD: 23.2,
    },
    {
      id: 'chest_right',
      view: 'front',
      laterality: 'right',
      group: 'torso',
      d: 'M115.9 113.8c-1.1 .1-2.3 .6-3.4 .9c-2.1 .5-4.2 .9-6.3 1.3c-6.6 1.1-13.2 1.9-19.8 3c-3 .5-9.1 1.4-11.2 3.8c-2.4 2.6-2.7 7-2.8 10.4c-.4 7.7 1.4 19.7 3.1 27.2c.3 1.6 2 2.2 2.2 3.8c.3 3-.2 12.9 .6 15c.6 1.6 7.1 4.7 8.9 5.3c9 3.5 15.6 1.9 24.1-2c1.6-.8 6.7-3.3 7.3-5c.4-1.3 .1-9.4 .1-11.5c0-8.3 0-16.6 0-24.9c0-7 0-14 0-21c0-2.2 .7-6.6-2.8-6.3z',
      centroid: { x: 95.6, y: 141.0 },
      ncicHint: 'R CHEST',
      inscribedD: 46.0,
    },
    {
      id: 'chest_left',
      view: 'front',
      laterality: 'left',
      group: 'torso',
      d: 'M123.7 113.8c-3.1 .2-2.4 4.1-2.4 6.2c0 7 0 14 0 21c0 8.3 0 16.6 0 25c0 2-.3 10.1 .1 11.4c.5 1.8 6 4.5 7.7 5.3c6.6 2.9 11.6 4.6 18.8 3.2c2.2-.5 4.3-1 6.2-1.9c1.6-.7 6.9-3.3 7.6-4.7c.6-1.5 .5-9.8 .6-12c0-1.1-.2-2.4 .1-3.5c.3-1.4 1.8-1.9 2.1-3.3c.4-1.6 .6-3.3 .9-4.9c1.3-7.3 2.5-14.9 2.2-22.3c-.1-3.4-.4-7.8-2.7-10.5c-1.8-2-6.7-3-9.3-3.4c-7.3-1.4-14.8-2.2-22.2-3.5c-1.4-.3-9.2-2.2-9.7-2.1z',
      centroid: { x: 144.3, y: 141.0 },
      ncicHint: 'L CHEST',
      inscribedD: 46.0,
    },
    {
      id: 'upper_arm_front_right',
      view: 'front',
      laterality: 'right',
      group: 'arm',
      d: 'M68.3 163.4c-1.5 .1-3.7 1.1-5.2 1.6c-3.4 .9-6.8 1.6-10.3 2c-1.7 .2-5.1 0-6.3 1.1c-1.1 1.1-1.1 3.8-1.3 5.3c-.7 4.1-1.6 8.1-2.3 12.3c-.5 3.4-.9 6.9-1.4 10.4c-.2 1.4-.5 3.1 1 4c1.1 .7 5.3 .2 6.9 .3c4.1 .3 7.3 1.6 11 3.1c1.1 .5 2.4 .3 3.1-.7c1-1.2 2-6.9 2.4-8.7c1.7-7.3 2.9-14.6 4.2-22c.3-1.6 1.2-5 1-6.4c-.2-1.4-1.4-2.4-2.8-2.3z',
      centroid: { x: 55.7, y: 183.4 },
      ncicHint: 'UR ARM',
      inscribedD: 24.4,
    },
    {
      id: 'upper_arm_front_left',
      view: 'front',
      laterality: 'left',
      group: 'arm',
      d: 'M171.3 163.4c-1.6 .1-2.6 1.6-2.4 3.1c.7 4.8 1.7 9.5 2.5 14.3c1 5.9 2.3 11.7 3.7 17.6c.5 1.8 .6 4.9 2.9 5.3c.5 .1 1 .1 1.5-.1c.7-.3 1.5-.7 2.2-1.1c2.3-.9 4.7-1.6 7.2-1.9c1.7-.3 3.3-.3 5-.3c.8 .1 1.7 .2 2.5 .2c.5-.1 1-.3 1.4-.6c1.3-1.1 .8-2.7 .7-4.2c-.5-3.5-1-6.9-1.5-10.4c-.6-4.1-1.6-8.2-2.3-12.3c-.2-1.4-.1-3.6-1.1-4.7c-1.1-1.3-4.1-1-5.7-1.2c-3.7-.4-7.3-1.1-10.8-2.1c-1.4-.4-4.6-1.7-5.8-1.6z',
      centroid: { x: 184.3, y: 183.5 },
      ncicHint: 'UL ARM',
      inscribedD: 24.4,
    },
    {
      id: 'elbow_front_right',
      view: 'front',
      laterality: 'right',
      group: 'arm',
      d: 'M47.3 202.9c-1.6 0-5 0-6.2 1.1c-1.2 1.1-1 3.3-1.2 4.7c-.4 3.3-1.2 6.5-2 9.8c-.3 1.3-1.1 3-.6 4.3c.6 1.6 3 2.5 4.4 3.1c3.9 1.9 8.2 2.6 12.6 2.3c.7-.1 1.7-.1 2.4-.4c.6-.2 1.2-.7 1.5-1.3c.7-1.5 1.2-6.8 1.6-8.8c.4-1.9 2.1-7.5 2.2-8.6c0-.4 0-.7-.1-1c-.4-1.2-1.6-1.7-2.6-2.2c-3.7-1.9-7.8-3-12-3z',
      centroid: { x: 49.1, y: 217.3 },
      ncicHint: 'R ARM',
      inscribedD: 21.2,
    },
    {
      id: 'elbow_front_left',
      view: 'front',
      laterality: 'left',
      group: 'arm',
      d: 'M192.5 202.9c-4 0-7.9 1-11.5 2.8c-1.1 .6-2.5 1.2-2.9 2.5c-.3 1.3 1.7 7.4 2 9.2c.4 1.9 1.1 8 1.8 9.3c.3 .5 .9 1 1.5 1.2c4.2 1.2 11.2-.2 15.1-2c1.3-.6 3.3-1.4 4-2.8c.7-1.3 0-3-.3-4.3c-.8-3.4-1.7-6.8-2.1-10.2c-.2-1.5-.1-3.6-1.3-4.7c-1.3-1.1-4.7-1-6.3-1z',
      centroid: { x: 190.6, y: 215.8 },
      ncicHint: 'L ARM',
      inscribedD: 21.2,
    },
    {
      id: 'forearm_front_right',
      view: 'front',
      laterality: 'right',
      group: 'arm',
      d: 'M37.8 227.3c-2.3 .2-2.5 2.4-2.8 4.2c-.8 4-1.4 8.2-1.9 12.3c-.9 8-1.6 15.9-2.3 23.9c-.2 1.9-.8 5.7-.7 7.4c.1 .5 .3 1 .6 1.4c.3 .4 .7 .7 1.2 .9c1.1 .3 2.4 .1 3.5 .2c1.5 .1 3 .4 4.4 .8c1.1 .3 2.2 1.1 3.3 1c2.4-.1 2.7-2.7 3.2-4.5c1.3-4.8 2.7-9.6 4-14.5c1.9-7 3.7-14.1 5.3-21.3c.5-1.9 2.1-6.1 .4-7.6c-.4-.4-.8-.6-1.3-.7c-.8-.1-1.7 .1-2.5 .1c-2.5 0-5-.4-7.4-1c-1.5-.5-6.1-2.7-7-2.6z',
      centroid: { x: 44.2, y: 241.1 },
      ncicHint: 'RF ARM',
      inscribedD: 21.2,
    },
    {
      id: 'forearm_front_left',
      view: 'front',
      laterality: 'left',
      group: 'arm',
      d: 'M201.8 227.3c-1.2 .1-3.3 1.4-4.6 1.9c-3.1 1.2-6.4 1.7-9.8 1.7c-1.5-.1-3.1-.3-3.9 1.3c-.8 1.6 .6 5.6 1 7.3c1.4 6.2 2.9 12.3 4.5 18.4c1.5 5.5 3 11 4.6 16.4c.5 1.9 .7 4.9 3.2 5.1c1.3 .1 2.9-.9 4.3-1.2c2.2-.6 4.1-.5 6.4-.7c.8-.1 1.6-.6 2-1.3c.8-1.4-.2-7.5-.3-9.4c-.8-8.3-1.5-16.6-2.6-24.9c-.5-3.8-1-7.6-1.7-11.3c-.4-1.7-1-3.5-3.1-3.3z',
      centroid: { x: 195.8, y: 241.2 },
      ncicHint: 'LF ARM',
      inscribedD: 21.2,
    },
    {
      id: 'wrist_right',
      view: 'front',
      laterality: 'right',
      group: 'arm',
      d: 'M33.4 280.1c-1.2 0-2.9-.2-3.7 .9c-.5 .8-1.4 8.6-1.8 10.3c-.2 .9-.9 2.4-.7 3.3c.7 3 10.8 4.5 13.3 3.3c1.8-.8 .7-2.1 .7-3.4c0-1 .3-2 .5-3c.4-1.5 2.1-6.6 1.9-7.7c-.3-2.7-7.9-3.7-10.2-3.7z',
      centroid: { x: 35.5, y: 287.6 },
      ncicHint: 'R WRS',
      inscribedD: 13.9,
    },
    {
      id: 'wrist_left',
      view: 'front',
      laterality: 'left',
      group: 'arm',
      d: 'M206.5 280.1c-2.2 0-9.2 1-10 3.4c-.4 1.2 1.3 6.1 1.7 7.7c.2 1 .7 2.3 .6 3.4c-.1 1.3-1.1 2.6 .8 3.4c2.6 1 11.8-.4 13-3c.5-1.1-.2-2.3-.4-3.4c-.4-1.6-1.5-10-2-10.7c-.8-1-2.6-.8-3.7-.8z',
      centroid: { x: 204.5, y: 287.6 },
      ncicHint: 'L WRS',
      inscribedD: 13.9,
    },
    {
      id: 'hand_palm_right',
      view: 'front',
      laterality: 'right',
      group: 'arm',
      d: 'M26 297.7c-1-.1-1.1 1.1-1.5 1.7c-.4 .6-1 .9-1.4 1.5c-.6 .9-1 2.1-1.5 3.2c-2.7 5.9-5.4 11.8-8.1 17.7c-.7 1.4-2.8 5-2.1 6.4c.3 .6 .9 1.1 1.5 1.2c3.2 .5 3.2-5.9 6.6-5.2c.5 .1 .9 .4 1.2 .9c.6 1-.2 3.6-.5 4.8c-.8 4.3-1.6 8.5-2.4 12.8c-.3 1.3-1.6 4.9 0 5.6c.7 .2 1.4 0 1.8-.6c.5-1 1.6-7.2 2-8.8c.3-1.3 1.4-2.5 2.8-1.6c.4 .3 .7 .7 .8 1.2c.2 1.3-.9 8.7-1.1 10.5c-.1 1.2-.7 3.6 1.2 3.7c1.6 0 1.6-2 1.7-3.1c.2-1.6 .9-8.5 1.4-9.4c.3-.5 .7-.8 1.2-.9c4.7-1 0 10.9 3 11.6c2 .4 1.8-2.2 1.9-3.4c.1-3.1 .3-6.3 .5-9.5c0-1.1-.1-2.5 1.1-3c.9-.5 2.1 0 2.4 1c.4 1.2 .4 5 .5 6.5c.1 .9 .2 3 1.7 2.2c1-.5 .6-3.3 .5-4.3c-.1-4.5-.2-9-.3-13.5c0-.6-.1-1.3 0-2c.3-1.1 .7-2.1 .8-3.3c.1-1.9 .1-3.7 .1-5.5c.1-2.7 .2-5.4 .2-8c0-1.1 .1-6.4-.3-7c-.2-.3-.4-.5-.8-.5c-.6-.1-1.4 .2-2 .2c-1.3 .2-2.7 .1-4 0c-2-.2-3.9-.7-5.8-1.5c-.8-.4-2.3-1.5-3.1-1.6z',
      centroid: { x: 29.5, y: 316.3 },
      ncicHint: 'R HND',
      inscribedD: 24.5,
    },
    {
      id: 'hand_palm_left',
      view: 'front',
      laterality: 'left',
      group: 'arm',
      d: 'M214.2 297.7c-.1 0-.3 0-.4 .1c-.6 .2-1.2 .7-1.8 .9c-1.3 .7-2.7 1.2-4.2 1.6c-2.1 .5-4.3 .7-6.4 .6c-.8-.1-2.3-.6-3 0c-.6 .6-.4 5.3-.4 6.4c-.1 3 .2 6 .2 9c0 1.8 0 3.7 .1 5.5c.2 2 .8 2.7 .8 4.9c-.1 4.5-.2 9-.3 13.5c0 .9-.4 3.8 .3 4.4c1.4 1.2 1.8-1 1.9-1.8c0-1.4 .1-6 .6-7c.3-.8 1.2-1.2 2.1-.9c1.6 .4 1.3 2.5 1.4 3.9c.1 2.8 .3 5.6 .4 8.5c.1 .9-.2 2.6 .7 3.3c1.1 .9 2.2-.3 2.2-1.4c0-1.8-.4-7.2-.1-8.5c.2-.9 1-1.5 1.8-1.4c.7 0 1.3 .5 1.6 1.1c.5 1.2 1.1 7.6 1.3 9.4c.1 1 .1 2.6 1.4 2.8c2.1 .4 1.6-2.2 1.5-3.4c-.2-1.7-1.4-9.9-1.1-10.9c.2-.5 .5-.9 1-1.1c1.5-.8 2.4 .6 2.7 1.9c.3 1.4 1.4 8 2.1 8.8c.3 .3 .8 .5 1.3 .4c2-.3 .7-4 .4-5.3c-.8-4.3-1.6-8.5-2.5-12.8c-.2-1.2-1.1-3.8-.6-4.9c.6-1.5 2.3-1.3 3.3-.4c1.5 1.3 2.2 5.2 4.8 4.4c.5-.1 .9-.4 1.2-.8c1-1.6-1.2-5-1.9-6.5c-2.8-6-5.6-12.1-8.3-18.2c-.5-1-.9-2.2-1.6-3.1c-.4-.5-1-.9-1.3-1.5c-.3-.5-.4-1.4-1.2-1.5z',
      centroid: { x: 210.5, y: 316.5 },
      ncicHint: 'L HND',
      inscribedD: 24.5,
    },
    {
      id: 'abdomen_upper',
      view: 'front',
      laterality: 'center',
      group: 'torso',
      d: 'M120.2 180.9c-1.6-.1-5.1 2.6-6.6 3.4c-4.6 2.3-9.6 4.4-14.7 4.7c-4.3 .2-9.2-.6-13.2-2.3c-1.8-.7-4.1-2.7-6-1.2c-.5 .4-.9 1-.9 1.7c-.3 1.6 .3 4.7 .4 6.4c.7 7.5 1.6 15 1 22.5c-.1 1.8-.9 5.5-.8 6.9c0 .7 .3 1.3 .8 1.8c1.1 1 3.3 .9 4.8 1.1c4.3 .6 8.6 1.2 12.9 1.6c15.1 1.4 30.3 1.3 45.4-.1c3.8-.4 7.6-.9 11.4-1.4c1.3-.2 3.8-.3 4.9-1c.5-.4 .9-1 1-1.7c.2-1.2-.7-5.3-.8-6.9c-.7-7.6 .3-15.3 1-22.9c.1-1.6 .7-5.1 .4-6.5c-.1-.7-.5-1.3-1.1-1.6c-1.9-1.3-4.2 .7-6 1.4c-3.9 1.6-8.5 2.4-12.7 2.2c-5.2-.2-10.5-2.4-15.1-4.8c-1.5-.8-4.7-3.2-6.1-3.3z',
      centroid: { x: 120.1, y: 206.1 },
      ncicHint: 'ABDOM',
      inscribedD: 44.8,
    },
    {
      id: 'abdomen_lower',
      view: 'front',
      laterality: 'center',
      group: 'torso',
      d: 'M80.5 227.9c-1 0-1.9 .5-2.4 1.4c-.6 1.1-2.2 8.3-2.8 10.2c-.3 1.2-1 2.6-.3 3.8c.7 1.4 3 3.6 4.2 4.9c1.8 2.2 3.6 4.4 5.4 6.6c7.7 9.2 15.7 18.1 23.2 27.5c2.6 3.2 4.9 6.7 7.5 10c1 1.3 2.3 3.3 3.9 3.8c.6 .2 1.3 .2 1.9-.1c2-.9 4.6-4.9 5.9-6.7c6.3-8.8 13.4-17.1 20.4-25.3c4.4-5 8.6-10.1 12.9-15.2c1.2-1.3 4.7-4.7 5-6.2c.2-1.1-2.6-10.2-3-12.1c-.3-1.2-.8-2.2-2.1-2.5c-1.3-.3-8.1 1-9.9 1.2c-6.8 .9-13.6 1.4-20.4 1.7c-11.2 .5-22.4 .1-33.5-.9c-3.5-.4-7-.8-10.4-1.3c-1.8-.3-3.7-.7-5.5-.8z',
      centroid: { x: 120.1, y: 256.7 },
      ncicHint: 'ABDOM',
      inscribedD: 51.1,
    },
    {
      id: 'hip_groin_right',
      view: 'front',
      laterality: 'right',
      group: 'torso',
      d: 'M74.8 248.3c-3-.2-3.4 4.5-4 6.6c-1.6 6.5-2.7 13-3.1 19.7c-.1 1.1-.3 2.4-.1 3.5c.1 .6 .4 1.2 1 1.6c1.4 1.1 5.6 2.4 7.4 3.1c7.9 3 16 5.5 24.1 7.9c1.6 .4 5.9 2 7.2 2c.5 0 1-.2 1.4-.5c.9-.6 1.3-1.6 1.1-2.6c-.4-1.7-4.9-6.8-6.2-8.4c-6.8-8.4-14-16.5-21-24.8c-1.4-1.7-4.9-6.4-6.4-7.6c-.4-.3-.9-.5-1.4-.5z',
      centroid: { x: 79.8, y: 271.4 },
      ncicHint: 'R HIP',
      inscribedD: 23.6,
    },
    {
      id: 'hip_groin_left',
      view: 'front',
      laterality: 'left',
      group: 'torso',
      d: 'M165.3 248.3c-.5 0-1 .2-1.4 .5c-1.4 1-5.1 5.9-6.5 7.5c-6.9 8.3-14.1 16.4-20.9 24.8c-1.3 1.5-5.9 6.8-6.2 8.4c-.3 1 .1 2 .9 2.6c.5 .4 1.2 .6 1.9 .6c1.9-.2 4.7-1.4 6.7-2c8.1-2.4 16.2-4.9 24.1-7.9c1.9-.7 6-2 7.4-3c.6-.4 1-1 1.1-1.6c.2-1.1 0-2.4-.1-3.5c-.4-6.6-1.4-13.2-3.1-19.7c-.6-2.1-1-6.8-3.9-6.7z',
      centroid: { x: 160.1, y: 271.5 },
      ncicHint: 'L HIP',
      inscribedD: 23.7,
    },
    {
      id: 'thigh_front_right',
      view: 'front',
      laterality: 'right',
      group: 'leg',
      d: 'M70.2 283.5c-3.7 .1-2.4 5.7-2.1 8c.8 9 1.8 17.9 3 26.8c1.8 13.9 3.8 27.7 6.3 41.5c.6 3.2 1.2 6.5 1.8 9.8c.2 1.3 .2 3.2 1.3 4.2c1.5 1.4 4.8-.1 6.7-.4c4.4-.8 8.9-.9 13.4-.3c1.6 .2 6.2 1.5 7.3 1.1c.7-.2 1.2-.7 1.5-1.3c.6-1.2 .6-5.8 .8-7.4c.7-9.3 1.7-18.6 2.6-27.8c.9-9 1.7-17.9 2.9-26.8c.4-2.5 .7-5 1.2-7.4c.1-.9 .6-1.6 .7-2.4c0-.3 0-.7-.2-1c-.1-.5-.4-.9-.8-1.2c-1.7-1.3-7.6-2.9-9.8-3.5c-8.3-2.5-16.7-4.8-24.8-7.7c-1.9-.6-10.7-4.2-11.8-4.2z',
      centroid: { x: 93.0, y: 314.4 },
      ncicHint: 'R THGH',
      inscribedD: 44.2,
    },
    {
      id: 'thigh_front_left',
      view: 'front',
      laterality: 'left',
      group: 'leg',
      d: 'M169.7 283.5c-1.2 0-9.8 3.6-11.7 4.2c-8.2 2.9-16.5 5.2-24.8 7.7c-2.2 .6-8.3 2.2-9.9 3.5c-.3 .3-.6 .8-.8 1.3c-.1 .3-.1 .6-.1 .9c.1 .9 .6 1.6 .7 2.4c.5 2.5 .9 4.9 1.2 7.4c1.2 8.8 2 17.5 2.8 26.3c.9 9.3 1.9 18.6 2.7 27.8c.1 1.7 .2 6.7 .8 8c.3 .5 .9 1 1.5 1.2c1.2 .4 5.8-.9 7.4-1.1c4.4-.6 9-.5 13.4 .3c1.9 .4 5.1 1.8 6.7 .4c1-1 1-2.9 1.2-4.3c.6-3.4 1.2-6.9 1.9-10.3c2.4-13.6 4.4-27.3 6.2-40.9c1.2-9 2.2-17.9 3-26.8c.3-2.3 1.6-8-2.2-8z',
      centroid: { x: 147.0, y: 314.4 },
      ncicHint: 'L THGH',
      inscribedD: 44.2,
    },
    {
      id: 'knee_right',
      view: 'front',
      laterality: 'right',
      group: 'leg',
      d: 'M95 375.3c-2.7 0-11.8 .7-13.5 2.7c-1.2 1.4-.2 5 0 6.8c.6 5.4 .9 10.9 1 16.4c0 1.6-.5 5.6 .4 6.8c1.3 1.7 7.4 2.5 9.5 2.6c3 .2 12.1-.4 14.1-2.6c1-1.1 .9-4.6 1.1-6.2c.3-3 .9-6 1.1-9c.2-2.6 .2-5.3 .2-7.9c.1-1.8 .8-5.5-.4-6.9c-1.6-1.9-10.9-2.7-13.5-2.7z',
      centroid: { x: 95.3, y: 388.8 },
      ncicHint: 'R KNEE',
      inscribedD: 26.8,
    },
    {
      id: 'knee_left',
      view: 'front',
      laterality: 'left',
      group: 'leg',
      d: 'M145 375.3c-2.7 0-11.9 .8-13.5 2.7c-1.2 1.4-.5 5.1-.4 6.9c0 2.6 0 5.3 .2 8c.2 3 .8 5.9 1.1 8.9c.2 1.6 .1 5.1 1.2 6.3c2 2.1 11.1 2.7 14 2.5c2.1-.1 8.2-.9 9.5-2.6c.9-1.2 .4-5.2 .4-6.8c.1-5.5 .4-11 1-16.5c.2-1.7 1.2-5.3 0-6.8c-1.6-1.9-10.9-2.6-13.5-2.6z',
      centroid: { x: 144.5, y: 389.0 },
      ncicHint: 'L KNEE',
      inscribedD: 26.8,
    },
    {
      id: 'shin_right',
      view: 'front',
      laterality: 'right',
      group: 'leg',
      d: 'M84.4 411.8c-2.8-.3-2.9 2.7-3.1 4.7c-.6 5.6-1 11.3-.8 16.9c.7 15.8 3.6 31.5 5.4 47.1c.5 4.3 .9 8.6 1.3 12.9c.1 1.5 0 4.2 .8 5.4c.4 .6 1 1 1.6 1.1c1.2 .2 3.6-.4 5-.5c1.4 0 3.7 .5 4.9 .3c3.3-.6 1.9-5.4 1.8-7.9c-.3-8.4 .4-16.6 1.4-24.9c1.8-13.6 4.4-27 4.6-40.7c0-2-.1-10.9-.6-12.4c-.1-.5-.4-.9-.8-1.2c-1.5-1.3-4.9 .1-6.7 .4c-3.8 .5-7.7 .4-11.5-.4c-1.1-.2-2.2-.7-3.3-.8z',
      centroid: { x: 93.8, y: 430.5 },
      ncicHint: 'R LEG',
      inscribedD: 26.7,
    },
    {
      id: 'shin_left',
      view: 'front',
      laterality: 'left',
      group: 'leg',
      d: 'M156.1 411.8c-1.2-.1-4.4 .9-5.9 1.2c-3.4 .5-7 .4-10.4-.2c-1.8-.3-4.2-1.5-5.8-.2c-1.1 1-.9 2.8-1 4.2c-.2 3.4-.3 6.7-.3 10c.3 14 3 27.8 4.8 41.6c1 8 1.6 15.9 1.2 23.9c-.1 2.3-1.4 6.9 1.8 7.4c1.2 .2 3.5-.3 5-.3c1.5 .1 3 .6 4.5 .5c.6 0 1.3-.3 1.7-.8c1.2-1.1 .9-3.3 1-4.7c.4-4.2 .8-8.3 1.3-12.5c1.7-15.8 4.6-31.6 5.5-47.5c.3-5.9-.2-11.7-.7-17.5c-.2-1.9-.1-5-2.7-5.1z',
      centroid: { x: 146.2, y: 430.1 },
      ncicHint: 'L LEG',
      inscribedD: 26.7,
    },
    {
      id: 'ankle_front_right',
      view: 'front',
      laterality: 'right',
      group: 'leg',
      d: 'M95.3 502c-1.6 0-5.5 .2-6.7 1.4c-.3 .4-.5 .9-.6 1.4c-.1 2.3 .1 4.7 0 7c0 1.5-.1 3-.4 4.5c-.4 2-2.3 5.9-.8 7.7c1 1.1 4.2 1.6 5.7 1.7c2.2 .3 8.4 .1 10.2-1.5c1-1 .7-2.9 .9-4.3c.2-1.1 .6-2.2 .7-3.4c.4-2.8-.5-5.6-.9-8.4c-.3-1.4-.1-3.7-1.3-4.7c-1.3-1.1-5.1-1.4-6.8-1.4z',
      centroid: { x: 96.0, y: 516.2 },
      ncicHint: 'R ANK',
      inscribedD: 16.5,
    },
    {
      id: 'ankle_front_left',
      view: 'front',
      laterality: 'left',
      group: 'leg',
      d: 'M144.8 502c-1.7 0-6 .3-7.2 1.7c-.8 1-.7 3-.9 4.3c-.5 2.7-1.4 5.6-1 8.4c.1 1.2 .4 2.3 .6 3.4c.3 1.3 0 3.3 1 4.3c1.4 1.6 7 1.9 9.1 1.8c1.7-.2 6.1-.7 7-2.2c1.1-2-.8-6.1-1.1-8.3c-.3-2.3-.4-4.6-.4-7c0-1.2 .3-2.7 0-4c-.1-.3-.2-.6-.4-.9c-1.1-1.3-5-1.5-6.7-1.5z',
      centroid: { x: 144.0, y: 515.9 },
      ncicHint: 'L ANK',
      inscribedD: 16.6,
    },
    {
      id: 'foot_top_right',
      view: 'front',
      laterality: 'right',
      group: 'leg',
      d: 'M87.9 527.5c-1.7 0-2 2.1-2.6 3.3c-1.5 3.4-3.9 8.7-2.9 12.4c.8 2.9 2.9 2.9 5.5 3.2c4.1 .5 13.8 .9 16.8-2.3c1.9-2 .5-10.1-.2-12.7c-.3-1.2-.6-3.4-1.9-3.8c-.8-.3-4.7 .7-5.9 .8c-2.1 .2-4.3 .1-6.4-.4c-.8-.1-1.7-.5-2.4-.5z',
      centroid: { x: 92.7, y: 537.6 },
      ncicHint: 'R FOOT',
      inscribedD: 18.3,
    },
    {
      id: 'foot_top_left',
      view: 'front',
      laterality: 'left',
      group: 'leg',
      d: 'M138.1 527.5c-1.8-.1-2 2-2.4 3.2c-1 3.3-1.8 8.5-1.2 11.9c1 4.8 13.7 4.3 17.4 3.8c1.5-.1 3.6-.1 4.7-1.3c3-3-.2-10.4-1.6-13.7c-.5-1-1.1-3.3-2.2-3.8c-.9-.4-4.1 .6-5.4 .8c-2.3 .2-4.6 .1-6.9-.4c-.8-.1-1.6-.5-2.4-.5z',
      centroid: { x: 147.4, y: 537.6 },
      ncicHint: 'L FOOT',
      inscribedD: 18.3,
    },
  ],
  back: [
    {
      id: 'head_back',
      view: 'back',
      laterality: 'center',
      group: 'head_neck',
      d: 'M120.1 19.4c-1.4-.1-5.3 .6-6.9 .8c-5.7 .8-11 3.8-13.8 8.9c-4 7.5-3.3 20.4-1.6 28.5c.6 2.5 2.3 10.7 3.5 12.5c.6 .8 1.6 1.3 2.3 1.9c4.6 3.7 10.5 6.2 16.5 6.2c5.9 0 11.6-2.4 16.1-6c.8-.6 1.7-1.2 2.3-2c1.2-1.4 2.5-7.4 3-9.5c2-8.3 2.8-16.8 1.1-25.2c-.5-2.8-1.3-5.6-3-7.9c-4.1-5.7-9-7-15.5-7.7c-1.3-.2-2.7-.5-4-.5z',
      centroid: { x: 119.9, y: 43.9 },
      ncicHint: 'HEAD',
      inscribedD: 46.8,
    },
    {
      id: 'neck_back',
      view: 'back',
      laterality: 'center',
      group: 'head_neck',
      d: 'M109.4 78.7c-1 0-2 .6-2.4 1.5c-.4 .8-.4 6.8-.6 8.4c-.3 2.1-.3 4.3-.7 6.4c-.2 1-1 1.7-1 2.8c.1 1.5 1.6 3.1 2.4 4.4c2.4 3.9 5.2 7.7 8.5 11c1.2 1.2 3.1 3.2 5 2.7c3.7-1 9.9-9.9 12-13.1c.8-1.3 2.5-3.4 2.6-4.8c.2-1.1-.6-1.8-.8-2.8c-.5-1.7-.3-3.7-.6-5.4c-.1-.5-.2-1-.3-1.5c-.2-2.2 0-5.8-.3-7.5c-.1-.4-.3-.9-.7-1.3c-1.6-1.7-4.1-.1-6 .4c-3.7 1-7.6 1.2-11.4 .4c-1.4-.3-4.7-1.6-5.7-1.6z',
      centroid: { x: 120.0, y: 96.0 },
      ncicHint: 'NECK',
      inscribedD: 28.5,
    },
    {
      id: 'shoulder_back_right',
      view: 'back',
      laterality: 'right',
      group: 'arm',
      d: 'M138.4 100.7c-1.7-.1-2.4 1.5-3.2 2.7c-1.4 2.3-2.9 4.5-4.6 6.6c-.9 1.2-2.1 2.2-2.8 3.5c-.7 1.2-.3 2.8 .8 3.5c.8 .5 1.9 .8 2.8 1.2c7.4 2.8 15.6 4.2 23.5 4.1c1.4 0 8.4-.7 9-.5c1.7 .4 3.3 1.7 4.2 3.2c3.4 6.2 2.1 17 1.3 23.7c-.3 1.7-1.6 7.7-1.5 8.9c.1 .5 .3 .9 .6 1.3c1 1.2 3.1 1.6 4.5 2.1c4.4 1.6 8.9 2.7 13.6 3.3c1.7 .3 4.4 1 5.8-.2c1.6-1.3 .7-3.9 .5-5.7c-.5-5.3-1-10.6-1.6-15.9c-.7-6.6-2.3-19.8-6.6-24.8c-2.4-3-6.1-4.9-9.9-5.2c-1 0-2 .2-3-.1c-1.1-.3-2.1-1-3.2-1.5c-1.3-.6-2.7-1.2-4.1-1.7c-5-1.8-10.2-3.1-15.3-4.6c-2.6-.7-5.1-1.6-7.6-2.7c-1-.4-2.1-1.2-3.2-1.2z',
      centroid: { x: 180.6, y: 151.4 },
      ncicHint: 'R SHLD',
      inscribedD: 23.0,
    },
    {
      id: 'shoulder_back_left',
      view: 'back',
      laterality: 'left',
      group: 'arm',
      d: 'M101.6 100.7c-.3-.1-.6 0-.9 .1c-1 .4-1.8 .9-2.7 1.3c-2.3 1-4.7 1.8-7.1 2.5c-5.1 1.5-10.3 2.7-15.3 4.6c-1.4 .5-2.8 1.1-4.2 1.7c-1 .5-2.1 1.1-3.2 1.5c-1.1 .3-2.3 0-3.4 .2c-3.7 .4-7.1 2.2-9.5 5c-4.3 5.1-5.9 18.2-6.6 24.9c-.6 5.3-1.1 10.6-1.6 15.9c-.2 1.7-1.1 4.3 .5 5.7c1.4 1.2 4.1 .5 5.8 .2c4.4-.5 8.8-1.7 13.1-3.1c1.3-.5 4-1.2 5-2.2c.3-.4 .5-.9 .6-1.4c.1-1.2-1.2-7.1-1.4-8.9c-.9-6.7-2.2-17.5 1.2-23.6c.8-1.6 2.5-2.8 4.2-3.3c.6-.2 7.5 .5 9 .5c7.9 .1 16.1-1.2 23.5-4.1c.9-.4 1.9-.7 2.7-1.2c1.2-.7 1.6-2.3 .9-3.5c-.7-1.2-1.9-2.3-2.8-3.5c-1.6-2.1-3.1-4.3-4.6-6.5c-.8-1.3-1.5-2.8-3.2-2.8z',
      centroid: { x: 59.4, y: 151.1 },
      ncicHint: 'L SHLD',
      inscribedD: 23.0,
    },
    {
      id: 'upper_back_right',
      view: 'back',
      laterality: 'right',
      group: 'torso',
      d: 'M124 118.1c-3.2-.1-2.7 3.4-2.7 5.5c0 5.8 0 11.6 0 17.4c0 8.4 0 16.7 0 25c0 1.9-.4 8.7 .3 10c.2 .4 .6 .8 1 1c1.7 1 6.5 2.3 8.5 3c8.6 2.6 13.3 4.4 22.5 2.4c2.4-.5 5-1.1 7.1-2.4c1.5-1 1.3-3 1.3-4.6c.2-3.6 .1-7.3 .3-11c.2-1.8 2-2.5 2.3-4.2c1.8-8.4 3.6-17.1 3-25.8c-.2-2.5-.5-7.2-2.4-9c-.6-.5-1.5-.9-2.3-1c-.9-.1-2 .2-3 .3c-1.9 .1-3.9 .2-5.9 .2c-6.7 0-13.4-1-19.7-3c-2-.6-9.2-3.8-10.3-3.8z',
      centroid: { x: 144.1, y: 147.2 },
      ncicHint: 'BACK',
      inscribedD: 45.4,
    },
    {
      id: 'upper_back_left',
      view: 'back',
      laterality: 'left',
      group: 'torso',
      d: 'M116.3 118.1c-1.1-.1-8.5 3.2-10.3 3.8c-6.7 2-13.8 3.1-20.7 3c-1.6 0-7.7-.6-8.5-.5c-.8 .2-1.6 .6-2.1 1.2c-1.9 2.1-2.2 6.5-2.3 9.1c-.5 7.6 1.2 18.3 3 25.8c.5 1.8 2.2 1.8 2.3 4.2c.1 3.7 .1 7.3 .3 11c.1 1.7-.1 3.5 1.6 4.4c2.2 1.2 4.6 1.9 7.1 2.4c9.1 1.9 13.9 0 22.4-2.6c1.8-.5 7.2-2.1 8.5-3c.4-.3 .7-.7 .9-1.2c.5-1.3 .2-8 .2-9.9c0-7.5 0-15 0-22.5c0-6.5 0-13 0-19.5c0-2.1 .5-5.5-2.4-5.7z',
      centroid: { x: 95.9, y: 147.2 },
      ncicHint: 'BACK',
      inscribedD: 45.4,
    },
    {
      id: 'upper_arm_back_right',
      view: 'back',
      laterality: 'right',
      group: 'arm',
      d: 'M171.3 163.4c-1.6 .1-2.6 1.6-2.4 3.1c.7 4.8 1.7 9.5 2.5 14.3c1 5.9 2.3 11.7 3.7 17.6c.5 1.8 .6 4.9 2.9 5.3c.5 .1 1 .1 1.5-.1c.7-.3 1.5-.7 2.2-1.1c2.3-.9 4.7-1.6 7.2-1.9c1.7-.3 3.3-.3 5-.3c.8 .1 1.7 .2 2.5 .2c.5-.1 1-.3 1.4-.6c1.3-1.1 .8-2.7 .7-4.2c-.5-3.5-1-6.9-1.5-10.4c-.6-4.1-1.6-8.2-2.3-12.3c-.2-1.4-.1-3.6-1.1-4.7c-1.1-1.3-4.1-1-5.7-1.2c-3.7-.4-7.3-1.1-10.8-2.1c-1.4-.4-4.6-1.7-5.8-1.6z',
      centroid: { x: 184.3, y: 183.5 },
      ncicHint: 'UR ARM',
      inscribedD: 24.4,
    },
    {
      id: 'upper_arm_back_left',
      view: 'back',
      laterality: 'left',
      group: 'arm',
      d: 'M68.3 163.4c-1.5 .1-3.7 1.1-5.2 1.6c-3.4 .9-6.8 1.6-10.3 2c-1.7 .2-5.1 0-6.3 1.1c-1.1 1.1-1.1 3.8-1.3 5.3c-.7 4.1-1.6 8.1-2.3 12.3c-.5 3.4-.9 6.9-1.4 10.4c-.2 1.4-.5 3.1 1 4c1.1 .7 5.3 .2 6.9 .3c4.1 .3 7.3 1.6 11 3.1c1.1 .5 2.4 .3 3.1-.7c1-1.2 2-6.9 2.4-8.7c1.7-7.3 2.9-14.6 4.2-22c.3-1.6 1.2-5 1-6.4c-.2-1.4-1.4-2.4-2.8-2.3z',
      centroid: { x: 55.7, y: 183.4 },
      ncicHint: 'UL ARM',
      inscribedD: 24.4,
    },
    {
      id: 'elbow_back_right',
      view: 'back',
      laterality: 'right',
      group: 'arm',
      d: 'M192.5 202.9c-4 0-7.9 1-11.5 2.8c-1.1 .6-2.5 1.2-2.9 2.5c-.3 1.3 1.7 7.4 2 9.2c.4 1.9 1.1 8 1.8 9.3c.3 .5 .9 1 1.5 1.2c4.2 1.2 11.2-.2 15.1-2c1.3-.6 3.3-1.4 4-2.8c.7-1.3 0-3-.3-4.3c-.8-3.4-1.7-6.8-2.1-10.2c-.2-1.5-.1-3.6-1.3-4.7c-1.3-1.1-4.7-1-6.3-1z',
      centroid: { x: 190.6, y: 215.8 },
      ncicHint: 'R ARM',
      inscribedD: 21.2,
    },
    {
      id: 'elbow_back_left',
      view: 'back',
      laterality: 'left',
      group: 'arm',
      d: 'M47.3 202.9c-1.6 0-5 0-6.2 1.1c-1.2 1.1-1 3.3-1.2 4.7c-.4 3.3-1.2 6.5-2 9.8c-.3 1.3-1.1 3-.6 4.3c.6 1.6 3 2.5 4.4 3.1c3.9 1.9 8.2 2.6 12.6 2.3c.7-.1 1.7-.1 2.4-.4c.6-.2 1.2-.7 1.5-1.3c.7-1.5 1.2-6.8 1.6-8.8c.4-1.9 2.1-7.5 2.2-8.6c0-.4 0-.7-.1-1c-.4-1.2-1.6-1.7-2.6-2.2c-3.7-1.9-7.8-3-12-3z',
      centroid: { x: 49.1, y: 217.3 },
      ncicHint: 'L ARM',
      inscribedD: 21.2,
    },
    {
      id: 'forearm_back_right',
      view: 'back',
      laterality: 'right',
      group: 'arm',
      d: 'M201.8 227.3c-1.2 .1-3.3 1.4-4.6 1.9c-3.1 1.2-6.4 1.7-9.8 1.7c-1.5-.1-3.1-.3-3.9 1.3c-.8 1.6 .6 5.6 1 7.3c1.4 6.2 2.9 12.3 4.5 18.4c1.5 5.5 3 11 4.6 16.4c.5 1.9 .7 4.9 3.2 5.1c1.3 .1 2.9-.9 4.3-1.2c2.2-.6 4.1-.5 6.4-.7c.8-.1 1.6-.6 2-1.3c.8-1.4-.2-7.5-.3-9.4c-.8-8.3-1.5-16.6-2.6-24.9c-.5-3.8-1-7.6-1.7-11.3c-.4-1.7-1-3.5-3.1-3.3z',
      centroid: { x: 195.8, y: 241.2 },
      ncicHint: 'RF ARM',
      inscribedD: 21.2,
    },
    {
      id: 'forearm_back_left',
      view: 'back',
      laterality: 'left',
      group: 'arm',
      d: 'M37.8 227.3c-2.3 .2-2.5 2.4-2.8 4.2c-.8 4-1.4 8.2-1.9 12.3c-.9 8-1.6 15.9-2.3 23.9c-.2 1.9-.8 5.7-.7 7.4c.1 .5 .3 1 .6 1.4c.3 .4 .7 .7 1.2 .9c1.1 .3 2.4 .1 3.5 .2c1.5 .1 3 .4 4.4 .8c1.1 .3 2.2 1.1 3.3 1c2.4-.1 2.7-2.7 3.2-4.5c1.3-4.8 2.7-9.6 4-14.5c1.9-7 3.7-14.1 5.3-21.3c.5-1.9 2.1-6.1 .4-7.6c-.4-.4-.8-.6-1.3-.7c-.8-.1-1.7 .1-2.5 .1c-2.5 0-5-.4-7.4-1c-1.5-.5-6.1-2.7-7-2.6z',
      centroid: { x: 44.2, y: 241.1 },
      ncicHint: 'LF ARM',
      inscribedD: 21.2,
    },
    {
      id: 'wrist_back_right',
      view: 'back',
      laterality: 'right',
      group: 'arm',
      d: 'M206.5 280.1c-2.2 0-9.2 1-10 3.4c-.4 1.2 1.3 6.1 1.7 7.7c.2 1 .7 2.3 .6 3.4c-.1 1.3-1.1 2.6 .8 3.4c2.6 1 11.8-.4 13-3c.5-1.1-.2-2.3-.4-3.4c-.4-1.6-1.5-10-2-10.7c-.8-1-2.6-.8-3.7-.8z',
      centroid: { x: 204.5, y: 287.6 },
      ncicHint: 'R WRS',
      inscribedD: 13.9,
    },
    {
      id: 'wrist_back_left',
      view: 'back',
      laterality: 'left',
      group: 'arm',
      d: 'M33.4 280.1c-1.2 0-2.9-.2-3.7 .9c-.5 .8-1.4 8.6-1.8 10.3c-.2 .9-.9 2.4-.7 3.3c.7 3 10.8 4.5 13.3 3.3c1.8-.8 .7-2.1 .7-3.4c0-1 .3-2 .5-3c.4-1.5 2.1-6.6 1.9-7.7c-.3-2.7-7.9-3.7-10.2-3.7z',
      centroid: { x: 35.5, y: 287.6 },
      ncicHint: 'L WRS',
      inscribedD: 13.9,
    },
    {
      id: 'hand_back_right',
      view: 'back',
      laterality: 'right',
      group: 'arm',
      d: 'M214.2 297.7c-.1 0-.3 0-.4 .1c-.6 .2-1.2 .7-1.8 .9c-1.3 .7-2.7 1.2-4.2 1.6c-2.1 .5-4.3 .7-6.4 .6c-.8-.1-2.3-.6-3 0c-.6 .6-.4 5.3-.4 6.4c-.1 3 .2 6 .2 9c0 1.8 0 3.7 .1 5.5c.2 2 .8 2.7 .8 4.9c-.1 4.5-.2 9-.3 13.5c0 .9-.4 3.8 .3 4.4c1.4 1.2 1.8-1 1.9-1.8c0-1.4 .1-6 .6-7c.3-.8 1.2-1.2 2.1-.9c1.6 .4 1.3 2.5 1.4 3.9c.1 2.8 .3 5.6 .4 8.5c.1 .9-.2 2.6 .7 3.3c1.1 .9 2.2-.3 2.2-1.4c0-1.8-.4-7.2-.1-8.5c.2-.9 1-1.5 1.8-1.4c.7 0 1.3 .5 1.6 1.1c.5 1.2 1.1 7.6 1.3 9.4c.1 1 .1 2.6 1.4 2.8c2.1 .4 1.6-2.2 1.5-3.4c-.2-1.7-1.4-9.9-1.1-10.9c.2-.5 .5-.9 1-1.1c1.5-.8 2.4 .6 2.7 1.9c.3 1.4 1.4 8 2.1 8.8c.3 .3 .8 .5 1.3 .4c2-.3 .7-4 .4-5.3c-.8-4.3-1.6-8.5-2.5-12.8c-.2-1.2-1.1-3.8-.6-4.9c.6-1.5 2.3-1.3 3.3-.4c1.5 1.3 2.2 5.2 4.8 4.4c.5-.1 .9-.4 1.2-.8c1-1.6-1.2-5-1.9-6.5c-2.8-6-5.6-12.1-8.3-18.2c-.5-1-.9-2.2-1.6-3.1c-.4-.5-1-.9-1.3-1.5c-.3-.5-.4-1.4-1.2-1.5z',
      centroid: { x: 210.5, y: 316.5 },
      ncicHint: 'R HND',
      inscribedD: 24.5,
    },
    {
      id: 'hand_back_left',
      view: 'back',
      laterality: 'left',
      group: 'arm',
      d: 'M26 297.7c-1-.1-1.1 1.1-1.5 1.7c-.4 .6-1 .9-1.4 1.5c-.6 .9-1 2.1-1.5 3.2c-2.7 5.9-5.4 11.8-8.1 17.7c-.7 1.4-2.8 5-2.1 6.4c.3 .6 .9 1.1 1.5 1.2c3.2 .5 3.2-5.9 6.6-5.2c.5 .1 .9 .4 1.2 .9c.6 1-.2 3.6-.5 4.8c-.8 4.3-1.6 8.5-2.4 12.8c-.3 1.3-1.6 4.9 0 5.6c.7 .2 1.4 0 1.8-.6c.5-1 1.6-7.2 2-8.8c.3-1.3 1.4-2.5 2.8-1.6c.4 .3 .7 .7 .8 1.2c.2 1.3-.9 8.7-1.1 10.5c-.1 1.2-.7 3.6 1.2 3.7c1.6 0 1.6-2 1.7-3.1c.2-1.6 .9-8.5 1.4-9.4c.3-.5 .7-.8 1.2-.9c4.7-1 0 10.9 3 11.6c2 .4 1.8-2.2 1.9-3.4c.1-3.1 .3-6.3 .5-9.5c0-1.1-.1-2.5 1.1-3c.9-.5 2.1 0 2.4 1c.4 1.2 .4 5 .5 6.5c.1 .9 .2 3 1.7 2.2c1-.5 .6-3.3 .5-4.3c-.1-4.5-.2-9-.3-13.5c0-.6-.1-1.3 0-2c.3-1.1 .7-2.1 .8-3.3c.1-1.9 .1-3.7 .1-5.5c.1-2.7 .2-5.4 .2-8c0-1.1 .1-6.4-.3-7c-.2-.3-.4-.5-.8-.5c-.6-.1-1.4 .2-2 .2c-1.3 .2-2.7 .1-4 0c-2-.2-3.9-.7-5.8-1.5c-.8-.4-2.3-1.5-3.1-1.6z',
      centroid: { x: 29.5, y: 316.3 },
      ncicHint: 'L HND',
      inscribedD: 24.5,
    },
    {
      id: 'mid_back_right',
      view: 'back',
      laterality: 'right',
      group: 'torso',
      d: 'M123.8 180.5c-2.8 .1-2.5 3.1-2.5 5.1c0 5.8 0 11.6 0 17.5c0 4.4 0 8.9 0 13.4c0 1.6-.5 4.1 .6 5.4c.4 .3 .8 .6 1.3 .8c1.3 .3 5.8-.1 7.4-.2c6.9-.2 13.7-.5 20.5-1.1c1.7-.1 6.6-.1 7.8-1c.6-.4 .9-1 1-1.7c.2-1.2-.3-4.9-.3-6.5c-.1-7 .8-13.9 1.4-20.9c.1-2 1.1-5.8-.8-7c-.5-.3-1-.5-1.5-.5c-1.2 .1-2.6 .7-3.8 1c-2.1 .5-4.3 .9-6.4 1c-5.8 .5-11.1-1.1-16.6-2.8c-1.4-.5-7.1-2.6-8.1-2.5z',
      centroid: { x: 139.8, y: 203.8 },
      ncicHint: 'BACK',
      inscribedD: 36.7,
    },
    {
      id: 'mid_back_left',
      view: 'back',
      laterality: 'left',
      group: 'torso',
      d: 'M116.1 180.5c-1.1 0-6.2 1.9-7.6 2.3c-5.7 1.8-11.1 3.5-17.1 3c-2.5-.2-4.9-.7-7.4-1.3c-.7-.1-1.5-.5-2.4-.6c-.6-.1-1.3 0-1.8 .4c-1.9 1.3-.9 5.2-.8 7.1c.6 7 1.5 13.9 1.4 20.9c0 1.5-.6 5.4-.3 6.5c.1 .5 .3 1 .7 1.3c1 1 2.9 .9 4.3 1c3.3 .3 6.6 .6 9.9 .8c5.8 .4 11.6 .6 17.5 .8c1.9 0 4.8 .7 5.9-1.3c.7-1.3 .3-7.7 .3-9.5c0-7.5 0-15 0-22.4c0-1.6 .3-6.3-.2-7.5c-.4-.9-1.4-1.5-2.4-1.5z',
      centroid: { x: 100.2, y: 203.8 },
      ncicHint: 'BACK',
      inscribedD: 36.8,
    },
    {
      id: 'lower_back_right',
      view: 'back',
      laterality: 'right',
      group: 'torso',
      d: 'M158.6 223.4c-2.5-.1-6.7 .6-9.4 .8c-6.1 .5-12.3 .7-18.4 .9c-1.9 .1-5.8-.1-7.5 .3c-.6 .2-1.2 .6-1.6 1.1c-.8 1.3-.4 4-.4 5.4c0 4.4 0 8.7 0 13c0 2.8 0 5.6 0 8.5c0 1.3-.1 2.7 0 4c.1 .5 .3 .9 .6 1.3c.5 .7 1.3 1 2.2 1c1.6-.1 6.2-2.2 7.9-2.9c8.2-3.2 16.4-6.5 24.6-9.6c2-.8 5.7-1.8 7.5-2.9c.4-.2 .7-.6 1-1c.7-1.5-.3-3.4-.7-4.8c-1.1-4-2.2-8.1-3.1-12.1c-.3-1.5-.9-3-2.7-3z',
      centroid: { x: 135.9, y: 239.6 },
      ncicHint: 'BACK',
      inscribedD: 29.2,
    },
    {
      id: 'lower_back_left',
      view: 'back',
      laterality: 'left',
      group: 'torso',
      d: 'M81.3 223.4c-1.8 0-2.4 1.5-2.7 3c-.8 4.1-1.9 8.1-3 12.1c-.4 1.4-1.4 3.3-.6 4.8c.2 .4 .5 .8 1 1.1c1.8 1 5.7 2.2 7.9 3c8.1 3.1 16.1 6.2 24.1 9.4c1.7 .7 6.4 2.8 8 2.9c.6 0 1.3-.2 1.8-.6c1.2-1.1 .9-2.8 .9-4.3c0-3.6 0-7.3 0-10.9c0-4.2 0-8.3 0-12.5c0-1.5 .4-3.6-.4-4.9c-.4-.5-1-.9-1.7-1.1c-1.7-.4-5.4-.2-7.4-.3c-6.2-.2-12.3-.4-18.4-.9c-2.6-.2-7.2-.9-9.5-.8z',
      centroid: { x: 104.1, y: 239.6 },
      ncicHint: 'BACK',
      inscribedD: 29.2,
    },
    {
      id: 'buttock_right',
      view: 'back',
      laterality: 'right',
      group: 'torso',
      d: 'M165.2 247c-1.4-.1-8.2 2.8-9.9 3.5c-7.6 2.9-15.2 5.9-22.8 8.9c-1.8 .8-9.8 3.3-10.6 4.3c-.3 .4-.5 .8-.6 1.3c-.3 2.5 0 7.3 0 10c0 5.4 0 10.7 0 16c0 1.6-.4 5.7 .2 7c.7 1.3 4.9 4.8 6.3 5.7c10 6.5 23.5 9.5 34.7 4.1c2.2-1.1 7.3-4.1 8.3-6.3c.6-1.3 .8-6.2 .9-7.9c.7-7 1.1-14 .5-21c-.6-6.6-1.8-13.2-3.5-19.6c-.6-2-.8-5.9-3.5-6z',
      centroid: { x: 147.0, y: 281.2 },
      ncicHint: 'R BUTTOCK',
      inscribedD: 51.0,
    },
    {
      id: 'buttock_left',
      view: 'back',
      laterality: 'left',
      group: 'torso',
      d: 'M75.2 247c-2.9-.2-3.2 3.7-3.8 5.7c-1.6 6.1-2.9 12.3-3.5 18.6c-.7 7.5-.4 15 .4 22.5c.1 1.7 .3 6.6 1 7.9c1.1 2.1 5.8 4.8 7.9 5.9c10.7 5.4 24.1 2.9 33.9-3.2c1.6-1.1 6.8-4.9 7.4-6.6c.5-1.2 .2-5.4 .2-7c0-5.1 0-10.3 0-15.4c0-2.3 .3-8.7 0-10.5c-.1-.5-.4-1-.7-1.4c-.8-.8-9.3-3.6-11.1-4.3c-7.3-2.9-14.6-5.8-21.9-8.6c-1.8-.7-8.4-3.5-9.8-3.6z',
      centroid: { x: 93.0, y: 281.4 },
      ncicHint: 'L BUTTOCK',
      inscribedD: 51.0,
    },
    {
      id: 'thigh_back_right',
      view: 'back',
      laterality: 'right',
      group: 'leg',
      d: 'M126.6 306.5c-3.4 .1-2.4 3.9-2.1 6c.9 7 1.6 13.9 2.3 20.9c.8 9.1 1.8 18.2 2.6 27.3c.2 1.9 .3 6.4 .8 7.9c.2 .7 .6 1.2 1.2 1.6c1.5 .8 3.8-.3 5.3-.5c3.6-.7 7.3-.9 11-.7c2.5 .1 4.9 .4 7.4 .9c1 .2 1.9 .5 2.9 .7c.5 0 1 0 1.5-.2c.6-.3 1.1-.8 1.3-1.4c.6-1.3 1.8-9.7 2.2-11.8c2.2-11.8 3.8-23.6 5.4-35.5c.3-2.1 1.6-9.3 1.4-10.9c-.1-.5-.3-1-.6-1.3c-1.5-1.8-3.4-.5-5 .3c-4.3 2.2-9.1 3.2-13.9 3.3c-5.5 .2-11.1-.9-16.1-3c-1.5-.6-6.5-3.6-7.6-3.6z',
      centroid: { x: 146.7, y: 333.3 },
      ncicHint: 'R THGH',
      inscribedD: 39.7,
    },
    {
      id: 'thigh_back_left',
      view: 'back',
      laterality: 'left',
      group: 'leg',
      d: 'M113.6 306.5c-1.3-.1-6 2.9-7.6 3.5c-4.9 2.1-10.3 3.2-15.6 3.1c-4.8 0-9.7-.9-14-3c-1.7-.8-3.7-2.5-5.4-.8c-.4 .4-.6 .9-.7 1.3c-.3 1.4 1 9 1.2 10.9c1.6 11.9 3.3 23.8 5.5 35.6c.5 2.7 1 5.5 1.5 8.3c.2 1.1 .2 2.4 .6 3.4c.2 .7 .7 1.2 1.3 1.5c.4 .2 .9 .3 1.4 .3c1.2-.1 2.3-.6 3.4-.8c2.3-.4 4.6-.7 7-.8c3.8-.2 7.6 0 11.4 .7c1.6 .3 3.8 1.4 5.3 .3c.4-.3 .7-.7 .9-1.2c.4-1.2 .7-6.7 .8-8.4c.8-9.3 1.8-18.6 2.7-27.9c.7-6.6 1.3-13.2 2.2-19.8c.2-2.1 1.4-5.9-1.9-6.2z',
      centroid: { x: 93.2, y: 333.2 },
      ncicHint: 'L THGH',
      inscribedD: 39.7,
    },
    {
      id: 'knee_back_right',
      view: 'back',
      laterality: 'right',
      group: 'leg',
      d: 'M145.3 371.5c-2.6 0-12.4 .7-14.1 2.6c-1.2 1.4-.4 5.5-.3 7.3c.3 5.5 .2 11 1 16.5c.2 1.5 .2 4.7 1.1 5.8c1.2 1.7 7.8 2.6 9.9 2.7c2.8 .2 12.3-.3 14-2.5c1-1.2 .6-3.4 .6-4.9c.2-4.1 .4-8.3 .8-12.4c.2-2.3 .5-4.7 .8-7c.2-1.2 .6-2.6 .6-3.9c0-.5-.1-1-.4-1.4c-1.5-2.2-11.3-2.8-14-2.8z',
      centroid: { x: 144.7, y: 385.3 },
      ncicHint: 'R KNEE',
      inscribedD: 27.3,
    },
    {
      id: 'knee_back_left',
      view: 'back',
      laterality: 'left',
      group: 'leg',
      d: 'M94.5 371.5c-2.5 0-12 .7-13.6 2.5c-.4 .5-.6 1.1-.6 1.8c0 1.3 .4 2.7 .6 4c.3 2.4 .6 4.9 .9 7.4c.3 3.8 .5 7.7 .6 11.5c.1 1.5-.3 3.6 .5 4.9c.8 1.4 2.9 1.7 4.4 2c4.1 1 8.3 1.2 12.4 .5c1.9-.2 6.4-.9 7.4-2.5c.7-1.1 .8-4.4 1-5.9c.8-5.4 .7-10.9 1-16.4c.1-1.7 .8-5.5-.1-6.9c-1.5-2.3-11.7-2.9-14.5-2.9z',
      centroid: { x: 95.3, y: 385.3 },
      ncicHint: 'L KNEE',
      inscribedD: 27.2,
    },
    {
      id: 'calf_right',
      view: 'back',
      laterality: 'right',
      group: 'leg',
      d: 'M155.2 407.7c-1.3 .1-2.7 .6-3.9 .9c-3.7 .6-7.4 .7-11 .2c-1.8-.3-4.8-1.5-6.3-.2c-1.1 1-.8 2.4-.8 3.7c0 3.2-.3 6.3-.4 9.5c-.6 13.8 2.1 27.6 3.9 41.2c1.1 7.8 2 15.6 2.1 23.4c0 2.3-.9 6.9 1.9 7.6c1.1 .3 2.4-.2 3.4-.3c1.4 0 2.7 0 4 .2c.8 .1 1.7 .4 2.5 .4c.7-.1 1.3-.4 1.7-.9c1.1-1.2 .8-3.4 1-4.9c.4-3.9 .8-7.9 1.3-11.9c.9-7.3 1.8-14.5 2.8-21.8c1.1-8.6 2.3-17.2 2.2-25.9c0-5.5-.6-11-1.3-16.4c-.3-2-.3-5-3.1-4.8z',
      centroid: { x: 146.2, y: 429.1 },
      ncicHint: 'R CALF',
      inscribedD: 26.7,
    },
    {
      id: 'calf_left',
      view: 'back',
      laterality: 'left',
      group: 'leg',
      d: 'M84.8 407.7c-2.8-.2-2.8 2.9-3.1 4.8c-.7 5.8-1.3 11.6-1.3 17.4c.1 8.5 1.2 17 2.3 25.4c.9 7.1 1.9 14.2 2.7 21.3c.5 4 .9 8 1.3 12c.2 1.4-.1 3.6 1 4.8c.4 .5 1 .8 1.7 .9c1.4 .1 3.1-.5 4.5-.6c1.6 0 3.6 .5 4.9 .4c3.4-.3 2.4-5.4 2.4-7.7c.1-8.4 1.1-16.6 2.3-24.9c1.9-13.3 4.3-26.7 3.7-40.2c0-1.2-.4-11.4-.5-11.5c-.1-.5-.4-.9-.8-1.2c-1.5-1.3-4.4-.1-6.2 .2c-3.7 .5-7.4 .4-11-.2c-1.3-.3-2.6-.8-3.9-.9z',
      centroid: { x: 93.8, y: 429.8 },
      ncicHint: 'L CALF',
      inscribedD: 26.7,
    },
    {
      id: 'heel_ankle_back_right',
      view: 'back',
      laterality: 'right',
      group: 'leg',
      d: 'M145.4 496.3c-1.7 0-5.4 .3-6.7 1.5c-1 1-1.1 4.3-1.4 5.8c-.4 3-1.9 9.6-1.7 12.3c.1 1.5 .6 2.9 .8 4.4c.2 1.5 .3 3 .3 4.5c0 .8-.2 1.7 0 2.5c.1 .3 .2 .6 .4 .9c1.2 1.3 5.5 1.6 7.2 1.7c1.9 0 7.4-.2 8.6-1.8c.8-1 .4-2.7 .6-3.9c.5-3.3-.4-4.6-1.1-7.8c-.4-2.1-.4-4.3-.5-6.4c0-2.3 0-4.7 .2-7c0-1.3 .4-2.7 .2-4c0-.3-.1-.6-.3-.9c-1-1.5-4.9-1.8-6.6-1.8z',
      centroid: { x: 145.0, y: 521.4 },
      ncicHint: 'R ANK',
      inscribedD: 16.8,
    },
    {
      id: 'heel_ankle_back_left',
      view: 'back',
      laterality: 'left',
      group: 'leg',
      d: 'M94.7 496.3c-1.6 0-5.5 .2-6.6 1.6c-.3 .5-.4 .9-.4 1.4c0 1.2 .2 2.4 .2 3.5c.2 2.4 .2 4.7 .2 7c0 2.2-.1 4.3-.5 6.5c-.6 3-1.6 4.5-1.1 7.8c.2 1.3-.2 3.3 .8 4.3c1.3 1.2 6.4 1.5 8.2 1.5c1.7-.1 6.1-.5 7.2-1.6c.4-.4 .6-.8 .6-1.3c.1-.7 0-1.4 0-2c0-1.5 .1-3 .3-4.5c.1-1.5 .6-2.9 .8-4.4c.2-2.8-1.2-9.3-1.7-12.4c-.2-1.4-.4-4.7-1.3-5.7c-1.2-1.3-5-1.7-6.7-1.7z',
      centroid: { x: 95.0, y: 521.4 },
      ncicHint: 'L ANK',
      inscribedD: 16.8,
    },
    {
      id: 'sole_right',
      view: 'back',
      laterality: 'right',
      group: 'leg',
      d: 'M153.7 531.1c-.9 0-2 .5-2.9 .7c-3.1 .8-6.3 .9-9.4 .4c-1.4-.2-4.3-1.3-5.4-.7c-1.7 1-2.1 10.4-1.1 12.1c2.1 3.7 13.4 3.3 17.2 2.8c1.9-.2 4.3-.2 5.2-2.2c1.4-3.2-.1-7.1-1.3-10.2c-.5-1.1-.8-2.8-2.3-2.9z',
      centroid: { x: 149.7, y: 539.4 },
      ncicHint: 'R FOOT',
      inscribedD: 14.4,
    },
    {
      id: 'sole_left',
      view: 'back',
      laterality: 'left',
      group: 'leg',
      d: 'M86.3 531.1c-1.5 0-1.8 1.8-2.3 2.9c-1.2 3.1-2.7 6.9-1.3 10.1c.9 2.1 3.3 2.1 5.2 2.3c3.8 .5 15 .9 17.2-2.8c1-1.7 .6-11-1.1-12.1c-1.1-.6-4 .5-5.3 .7c-3.2 .5-6.4 .4-9.5-.3c-.9-.3-1.9-.8-2.9-.8z',
      centroid: { x: 89.9, y: 539.4 },
      ncicHint: 'L FOOT',
      inscribedD: 14.4,
    },
  ],
}

/**
 * Which of the subject's sides sits on the viewer's left / right in each view. Render the
 * side badges from this (t('humint.bodyMap.sideShort.left') etc.), never from x alone.
 */
export const BODY_SIDE_CUES: Record<BodyView, { viewerLeft: Laterality; viewerRight: Laterality }> =
  {
    front: { viewerLeft: 'right', viewerRight: 'left' },
    back: { viewerLeft: 'left', viewerRight: 'right' },
  }

const REGION_INDEX = new Map<BodyRegionId, BodyRegion>(
  [...BODY_REGIONS.front, ...BODY_REGIONS.back].map(region => [region.id, region])
)

/** Look up a region of either view by id. */
export function getBodyRegion(id: BodyRegionId): BodyRegion | undefined {
  return REGION_INDEX.get(id)
}
